import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { KeyRound, Laptop, Lightbulb, Mail, Shield, Smartphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuthStore } from "@/stores/auth-store";
import { AuthCard } from "@/components/shell/auth-card";
import { cn } from "@/lib/utils";

type MfaMethod = "passkey" | "authenticator" | "hardware";

// Mock-only: a code of all zeros simulates the "wrong code" path so the
// error state can be exercised without a real backend — matches the
// validated prototype's own convention (pages/auth/mfa.html, verifyTOTP()).
// Real validation will replace this once API integration lands.
const MOCK_INVALID_CODE = "000000";
const CODE_LIFETIME_SECONDS = 30;
const MAX_ATTEMPTS = 3;

function useMfaVerify() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get("returnTo") ?? "/tenant-select";
  const goTenantSelect = useAuthStore((s) => s.goTenantSelect);
  const setProcessing = useAuthStore((s) => s.setProcessing);
  return useCallback(() => {
    setProcessing(true);
    setTimeout(() => {
      goTenantSelect();
      navigate(`/tenant-select?returnTo=${encodeURIComponent(returnTo)}`);
    }, 900);
  }, [goTenantSelect, setProcessing, navigate, returnTo]);
}

function AuthenticatorCode({ onComplete }: { onComplete: (code: string) => void }) {
  const [digits, setDigits] = useState<string[]>(["", "", "", "", "", ""]);
  const [invalid, setInvalid] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [secondsLeft, setSecondsLeft] = useState(CODE_LIFETIME_SECONDS);
  const refs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    refs.current[0]?.focus();
  }, []);

  useEffect(() => {
    const id = setInterval(() => {
      setSecondsLeft((s) => (s <= 1 ? CODE_LIFETIME_SECONDS : s - 1));
    }, 1000);
    return () => clearInterval(id);
  }, []);

  const complete = digits.every((d) => d !== "");

  function setDigit(i: number, v: string) {
    const next = [...digits];
    next[i] = v;
    setDigits(next);
    setInvalid(false);
  }

  // Explicit submit — does not auto-fire on the 6th digit — matching both
  // the validated prototype and the canonical LOG-02b Figma frame.
  function verify() {
    if (!complete) return;
    const code = digits.join("");
    if (code === MOCK_INVALID_CODE) {
      setAttempts((a) => a + 1);
      setInvalid(true);
      setDigits(["", "", "", "", "", ""]);
      refs.current[0]?.focus();
      return;
    }
    onComplete(code);
  }

  return (
    <div className="flex w-full flex-col gap-3">
      <p className="text-center text-sm text-muted">Enter the 6-digit code from your authenticator app</p>

      <div className="flex gap-2" role="group" aria-label="Six-digit verification code">
        {digits.map((d, i) => (
          <input
            key={i}
            ref={(el) => { refs.current[i] = el; }}
            inputMode="numeric"
            maxLength={1}
            aria-label={`Digit ${i + 1}`}
            aria-invalid={invalid}
            value={d}
            onChange={(e) => {
              const v = e.target.value.replace(/\D/g, "");
              if (v) setDigit(i, v);
            }}
            onKeyDown={(e) => {
              if (e.key === "Backspace" && !digits[i]) {
                if (i > 0) refs.current[i - 1]?.focus();
              } else if (e.key === "ArrowLeft" && i > 0) {
                refs.current[i - 1]?.focus();
              } else if (e.key === "ArrowRight" && i < 5) {
                refs.current[i + 1]?.focus();
              } else if (e.key === "Enter" && complete) {
                verify();
              }
            }}
            onInput={(e) => {
              if (e.currentTarget.value && i < 5) refs.current[i + 1]?.focus();
            }}
            className={cn(
              "h-12 w-full min-w-0 rounded-sm border text-center text-lg font-bold outline-none transition-colors",
              invalid
                ? "border-danger text-danger focus:border-danger focus:shadow-[0_0_0_3px_var(--ads-danger-soft)]"
                : d
                ? "border-primary bg-primary-soft text-ink focus:border-primary focus:shadow-[0_0_0_3px_var(--ads-primary-soft)]"
                : "border-line-strong text-ink focus:border-primary focus:shadow-[0_0_0_3px_var(--ads-primary-soft)]"
            )}
          />
        ))}
      </div>

      <p className={cn("text-center text-[11px]", secondsLeft <= 5 ? "font-semibold text-danger" : "text-muted")}>
        Code refreshes in <span className="tabular-nums" data-testid="mfa-countdown">{secondsLeft}</span> seconds
      </p>

      {invalid && (
        <div className="rounded border border-danger-border bg-danger-soft px-3 py-2.5 text-center" role="alert">
          <p className="text-xs leading-relaxed text-danger">
            Incorrect code. Please try again. ({Math.max(0, MAX_ATTEMPTS - attempts)} attempts remaining)
          </p>
        </div>
      )}

      <Button className="w-full" onClick={verify} disabled={!complete}>
        Verify Code
      </Button>

      <div className="flex flex-wrap items-center justify-center gap-1.5 text-xs font-medium">
        <button type="button" className="text-primary outline-none hover:underline">Use a recovery code instead</button>
        <span className="text-muted">·</span>
        <UseAnotherMethodLink />
      </div>
    </div>
  );
}

function UseAnotherMethodLink() {
  const goLogin = useAuthStore((s) => s.goLogin);
  const navigate = useNavigate();
  return (
    <button
      type="button"
      className="text-primary outline-none hover:underline"
      onClick={() => { goLogin(); navigate("/login"); }}
    >
      Use a different method
    </button>
  );
}

const TITLE: Record<MfaMethod, string> = {
  passkey: "Verify Your Identity",
  authenticator: "Enter Verification Code",
  hardware: "Insert Security Key",
};

function resolveMethod(method: string | null): MfaMethod {
  if (method === "passkey") return "passkey";
  if (method === "hardware") return "hardware";
  return "authenticator";
}

export default function MfaPage() {
  const method = useAuthStore((s) => s.method);
  const userEmail = useAuthStore((s) => s.user?.email);
  const confirm = useMfaVerify();
  const activeMethod = resolveMethod(method);

  return (
    <AuthCard>
      <div className="auth-icon" aria-hidden="true">
        <div className="auth-icon__inner">
          <Mail size={28} strokeWidth={1.25} />
        </div>
      </div>

      <div className="flex w-full flex-col gap-6">
        <div className="auth-heading">
          <h1 className="auth-title">{TITLE[activeMethod]}</h1>
          {userEmail && <p className="auth-subtitle">{userEmail}</p>}
        </div>

        {activeMethod === "passkey" && (
          <div className="flex w-full flex-col items-center gap-3 text-center">
            <button
              type="button"
              onClick={confirm}
              aria-label="Verify with passkey"
              className="flex h-16 w-16 items-center justify-center rounded-[18px] bg-gradient-to-br from-success to-success-vivid outline-none transition-transform hover:scale-105 focus-visible:ring-2 focus-visible:ring-primary-soft"
            >
              <KeyRound className="size-8 text-white" aria-hidden />
            </button>
            <p className="text-sm font-bold text-ink">Use Your Passkey</p>
            <p className="max-w-[320px] text-xs leading-relaxed text-muted">
              Touch your fingerprint sensor, use Face ID, or insert your security key when prompted by your browser.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <span className="flex items-center gap-1.5 rounded-lg border border-line bg-white px-3.5 py-2 text-xs font-medium text-muted">
                <Laptop className="size-3.5" aria-hidden /> This device
              </span>
              <span className="flex items-center gap-1.5 rounded-lg border border-line bg-white px-3.5 py-2 text-xs font-medium text-muted">
                <Smartphone className="size-3.5" aria-hidden /> Phone
              </span>
              <span className="flex items-center gap-1.5 rounded-lg border border-line bg-white px-3.5 py-2 text-xs font-medium text-muted">
                <Shield className="size-3.5" aria-hidden /> Security key
              </span>
            </div>
            <UseAnotherMethodLink />
          </div>
        )}

        {activeMethod === "authenticator" && <AuthenticatorCode onComplete={confirm} />}

        {activeMethod === "hardware" && (
          <div className="flex w-full flex-col items-center gap-3 text-center">
            <button
              type="button"
              onClick={confirm}
              aria-label="Authenticate with security key"
              className="flex h-16 w-16 items-center justify-center rounded-[18px] bg-gradient-to-br from-pending to-pending-dark outline-none transition-transform hover:scale-105 focus-visible:ring-2 focus-visible:ring-primary-soft"
            >
              <Smartphone className="size-8 text-white" aria-hidden />
            </button>
            <p className="text-sm font-bold text-ink">Touch Your Security Key</p>
            <p className="max-w-[320px] text-xs leading-relaxed text-muted">
              Insert your YubiKey or other FIDO2 security key and tap it when the light flashes.
            </p>
            <div className="flex items-start gap-2 rounded-lg bg-primary-soft px-3.5 py-2.5 text-left">
              <Lightbulb className="mt-0.5 size-3.5 shrink-0 text-primary" aria-hidden />
              <p className="text-xs leading-relaxed text-primary">
                If your key has a fingerprint reader, place your finger on it. If it has a button, press it when the light blinks.
              </p>
            </div>
            <UseAnotherMethodLink />
          </div>
        )}
      </div>
    </AuthCard>
  );
}
